import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Typography, Popover, Checkbox, CircularProgress, } from "@mui/material";
import { Search as SearchIcon, Send as SendIcon } from "lucide-react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import PageContainer from "../../common_components/PageContainer";
import { TextInput, Dropdown } from "../../common_components/FormInputs";
import AppButton from "../../common_components/AppButton";
import CommonTable from "../../common_components/CommonTable";
import CommonChip from "../../common_components/CommonChip";
import CommonTabs from "../../common_components/CommonTabs";
import { toast } from "../../common_components/Toast/ToastUtils";
import SemrushToolHeader from "./Component/SemrushToolHeader";
import { selectToken } from "../../redux/authSlice";
import { semrushService, } from "../../services/semrush.service";
const SCOPE_TABS = [
    { key: "worldwide", label: "Worldwide", icon: "/assets/earth.svg" },
    { key: "us", label: "US", icon: "/assets/us.svg" },
    { key: "uk", label: "UK", icon: "/assets/uk.svg" },
    { key: "de", label: "DE", icon: "/assets/de.svg" },
];
const SEARCH_TYPE_OPTIONS = [
    { value: "root_domain", label: "Root Domain" },
    { value: "exact_url", label: "Exact URL" },
];
// Fixed five rows for the distribution table — rendered even before search.
const DISTRIBUTION_ROW_TEMPLATE = [
    { key: "worldwide", label: "Worldwide" },
    { key: "us", label: "US", icon: "/assets/us.svg" },
    { key: "uk", label: "UK", icon: "/assets/uk.svg" },
    { key: "de", label: "DE", icon: "/assets/de.svg" },
    { key: "other", label: "Other" },
];
const EMPTY_TILE = {
    current: {
        rank: null,
        organic_traffic: 0,
        organic_keyword: 0,
        paid_traffic: 0,
        paid_keyword: 0,
    },
    previous: {
        rank: null,
        organic_traffic: 0,
        organic_keyword: 0,
        paid_traffic: 0,
        paid_keyword: 0,
    },
    change: {
        organic_traffic_pct: 0,
        organic_keyword_pct: 0,
        paid_traffic_pct: 0,
        paid_keyword_pct: 0,
        rank_delta: null,
    },
};
const compactNumber = (value) => {
    if (value === null || value === undefined || !Number.isFinite(value))
        return "0";
    const abs = Math.abs(value);
    if (abs >= 1000000000)
        return `${(value / 1000000000).toFixed(1).replace(/\.0$/, "")}B`;
    if (abs >= 1000000)
        return `${(value / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
    if (abs >= 1000)
        return `${(value / 1000).toFixed(1).replace(/\.0$/, "")}K`;
    return String(Math.round(value));
};
const fmtShare = (value) => {
    if (value === null || value === undefined || !Number.isFinite(value))
        return "0%";
    return `${Math.round(value)}%`;
};
// CommonChip color palette indexes from CommonChip.tsx:
//   index 0 → red (B42318 on FEF2F2), index 2 → green (067647 on D1FADF)
const CHIP_INDEX_UP = 2;
const CHIP_INDEX_DOWN = 0;
const DeltaChip = ({ value }) => {
    const v = value ?? 0;
    const isDown = Number.isFinite(v) && v < 0;
    const pct = Math.abs(Math.round(Number.isFinite(v) ? v : 0));
    const label = (_jsxs(Box, { component: "span", sx: { display: "inline-flex", alignItems: "center", gap: "4px" }, children: [_jsxs("span", { children: [pct, "%"] }), _jsx("img", { src: isDown ? "/assets/trending-down.svg" : "/assets/trend-up.svg", alt: "", width: 12, height: 12 })] }));
    return (_jsx(CommonChip, { label: label, index: isDown ? CHIP_INDEX_DOWN : CHIP_INDEX_UP, renderType: "tag", sx: { fontSize: "14px" } }));
};
const RankDeltaChip = ({ rank, delta }) => {
    const rankVal = rank !== null && rank !== undefined && rank !== "" ? Number(rank) : 0;
    // red + down icon if rank > 0, green + up icon if rank <= 0
    const isPositive = rankVal > 0;
    const label = (_jsxs(Box, { component: "span", sx: { display: "inline-flex", alignItems: "center", gap: "4px" }, children: [_jsx("span", { children: rankVal }), _jsx("img", { src: isPositive ? "/assets/trending-down.svg" : "/assets/trend-up.svg", alt: "", width: 12, height: 12 })] }));
    return (_jsx(CommonChip, { label: label, index: isPositive ? CHIP_INDEX_DOWN : CHIP_INDEX_UP, renderType: "tag", sx: { fontSize: "14px" } }));
};
const SemrushToolMain = () => {
    const token = useSelector(selectToken) || "";
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchInput, setSearchInput] = useState("");
    const [searchType, setSearchType] = useState("root_domain");
    const [searchError, setSearchError] = useState("");
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [searchedTerm, setSearchedTerm] = useState("");
    const [scope, setScope] = useState("worldwide");
    const [distributionMode, setDistributionMode] = useState("organic");
    // ── Share dialog state ──
    const [shareAnchor, setShareAnchor] = useState(null);
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [usersLoaded, setUsersLoaded] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [userSearch, setUserSearch] = useState("");
    const [sharing, setSharing] = useState(false);
    const shareOpen = Boolean(shareAnchor);
    const handleSearch = useCallback(async () => {
        const term = searchInput.trim();
        if (!term) {
            setSearchError("Please enter a domain or URL");
            return;
        }
        setSearchError("");
        setLoading(true);
        try {
            const resp = await semrushService.getDashboard(term, searchType, token);
            if (resp?.success && resp.data) {
                setData(resp.data);
                setSearchedTerm(term);
                setScope("worldwide");
                setDistributionMode("organic");
                // Clear any stale shared-report id from the URL on a fresh search.
                if (searchParams.get("record")) {
                    searchParams.delete("record");
                    setSearchParams(searchParams, { replace: true });
                }
            }
            else {
                setData(null);
                toast.error(resp?.message || "Failed to fetch Semrush data");
            }
        }
        catch (err) {
            setData(null);
            toast.error(err?.message || "Failed to fetch Semrush data");
        }
        finally {
            setLoading(false);
        }
    }, [searchInput, searchType, token, searchParams, setSearchParams]);
    // Open a shared report directly from a ?record=<id> deep link (email "here"
    // button) — restores the dashboard at the same position from the DB.
    useEffect(() => {
        const recordId = searchParams.get("record");
        if (!recordId || !token)
            return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const resp = await semrushService.getRecord(recordId, token);
                if (cancelled)
                    return;
                if (resp?.success && resp.data) {
                    setData(resp.data);
                    setSearchInput(resp.data.search || "");
                    setSearchedTerm(resp.data.search || "");
                    setSearchType(resp.data.search_type || "root_domain");
                    setScope("worldwide");
                    setDistributionMode("organic");
                }
                else {
                    toast.error(resp?.message || "Shared report could not be loaded");
                }
            }
            catch (err) {
                if (!cancelled)
                    toast.error(err?.message || "Shared report could not be loaded");
            }
            finally {
                if (!cancelled)
                    setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // Only react to the record id / token changing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams.get("record"), token]);
    // ── Share handlers ──
    const openShare = useCallback(async (e) => {
        setShareAnchor(e.currentTarget);
        setSelectedUserIds([]);
        setUserSearch("");
        if (!usersLoaded && !usersLoading) {
            setUsersLoading(true);
            try {
                const list = await semrushService.getShareableUsers(token);
                setUsers(list);
                setUsersLoaded(true);
            }
            catch (err) {
                toast.error(err?.message || "Failed to load users");
            }
            finally {
                setUsersLoading(false);
            }
        }
    }, [token, usersLoaded, usersLoading]);
    const closeShare = useCallback(() => setShareAnchor(null), []);
    const toggleUser = useCallback((id) => {
        setSelectedUserIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    }, []);
    const filteredUsers = useMemo(() => {
        const q = userSearch.trim().toLowerCase();
        if (!q)
            return users;
        return users.filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
    }, [users, userSearch]);
    const handleShare = useCallback(async () => {
        if (!data?.record_id) {
            toast.error("Run a search before sharing.");
            return;
        }
        if (selectedUserIds.length === 0) {
            toast.error("Select at least one user to share with.");
            return;
        }
        setSharing(true);
        try {
            const resp = await semrushService.shareReport(data.record_id, selectedUserIds, token);
            if (resp?.success) {
                toast.success(resp?.message || "Report shared successfully.");
                closeShare();
            }
            else {
                toast.error(resp?.message || "Failed to share report");
            }
        }
        catch (err) {
            toast.error(err?.message || "Failed to share report");
        }
        finally {
            setSharing(false);
        }
    }, [data, selectedUserIds, token, closeShare]);
    const handleKeyDown = useCallback((e) => {
        if (e.key === "Enter")
            handleSearch();
    }, [handleSearch]);
    const activeTile = useMemo(() => {
        return data?.tiles?.[scope] ?? EMPTY_TILE;
    }, [data, scope]);
    // Build the 5-row distribution data, merging API rows by key.
    const distributionRows = useMemo(() => {
        const byKey = {};
        (data?.distribution ?? []).forEach((r) => {
            byKey[r.key] = r;
        });
        return DISTRIBUTION_ROW_TEMPLATE.map((tpl) => {
            const r = byKey[tpl.key];
            const block = r ? r[distributionMode] : null;
            return {
                key: tpl.key,
                label: tpl.label,
                icon: tpl.icon,
                traffic_share: block?.traffic_share ?? 0,
                traffic: block?.traffic ?? 0,
                keywords: block?.keywords ?? 0,
            };
        });
    }, [data, distributionMode]);
    // Header renderer — label only, no sort icons (per design).
    const renderTableHeader = useCallback((column) => (_jsx(Box, { sx: { display: "flex", alignItems: "center", width: "100%" }, children: _jsx(Typography, { sx: {
                fontSize: "12px",
                fontWeight: 700,
                color: "#252b37",
                letterSpacing: "0.2px",
            }, children: column.label }) })), []);
    const columns = useMemo(() => [
        {
            id: "country",
            label: "Countries",
            width: "240px",
            align: "left",
            sortable: false,
            draggable: false,
            renderHeader: renderTableHeader,
        },
        {
            id: "traffic_share",
            label: "Traffic Share",
            width: "280px",
            align: "left",
            sortable: false,
            draggable: false,
            renderHeader: renderTableHeader,
        },
        {
            id: "traffic",
            label: "Traffic",
            width: "180px",
            align: "left",
            sortable: false,
            draggable: false,
            renderHeader: renderTableHeader,
        },
        {
            id: "keywords",
            label: "Keywords",
            width: "180px",
            align: "left",
            sortable: false,
            draggable: false,
            renderHeader: renderTableHeader,
        },
    ], [renderTableHeader]);
    const renderCell = useCallback((row, column) => {
        switch (column.id) {
            case "country":
                return (_jsxs(Box, { className: "semrush-tool-country-cell", children: [row.icon && (_jsx("img", { src: row.icon, alt: row.label, className: "semrush-tool-row-icon" })), _jsx("span", { children: row.label })] }));
            case "traffic_share":
                return (_jsxs(Box, { className: "semrush-tool-share-cell", children: [_jsx(Box, { className: "semrush-tool-share-bar-wrapper", children: _jsx(Box, { className: "semrush-tool-share-bar", sx: {
                                    width: `${Math.min(100, Math.max(0, row.traffic_share))}%`,
                                } }) }), _jsx(Typography, { className: "semrush-tool-share-text", children: fmtShare(row.traffic_share) })] }));
            case "traffic":
                return _jsx(Typography, { sx: { fontSize: "14px !important", color: "#111827" }, children: compactNumber(row.traffic) });
            case "keywords":
                return _jsx(Typography, { sx: { fontSize: "14px !important", color: "#111827" }, children: compactNumber(row.keywords) });
            default:
                return null;
        }
    }, []);
    return (_jsxs(PageContainer, { children: [_jsx(SemrushToolHeader, {}), _jsx(Box, { className: "semrush-tool-search-card", children: _jsxs(Box, { className: "semrush-tool-search-row", children: [_jsx(Box, { className: "semrush-tool-search-input", children: _jsx(TextInput, { placeholder: "Enter domain, subdomain or URL", value: searchInput, onChange: (e) => {
                                    setSearchInput(e.target.value);
                                    if (searchError)
                                        setSearchError("");
                                }, onKeyDown: handleKeyDown, error: !!searchError, errorMessage: searchError, fullWidth: true }) }), _jsx(Box, { className: "semrush-tool-search-type", children: _jsx(Dropdown, { value: searchType, onChange: (value) => setSearchType(value), options: SEARCH_TYPE_OPTIONS, placeholder: "Root Domain", fullWidth: true, sortByLabel: false }) }), _jsx(Button, { variant: "outlined", color: "inherit", className: "semrush-tool-action-button", startIcon: _jsx(SearchIcon, { size: 16 }), onClick: handleSearch, disabled: loading, children: "Search" }), _jsx(AppButton, { variant: "outlined", color: "inherit", className: "semrush-tool-action-button", startIcon: _jsx(SendIcon, { size: 16 }), disabled: !data || !data.record_id, onClick: openShare, children: "Share" })] }) }), _jsxs(Popover, { open: shareOpen, anchorEl: shareAnchor, onClose: closeShare, anchorOrigin: { vertical: "bottom", horizontal: "right" }, transformOrigin: { vertical: "top", horizontal: "right" }, slotProps: { paper: { className: "semrush-share-popover" } }, children: [_jsxs(Box, { className: "semrush-share-header", children: [_jsx(Typography, { className: "semrush-share-title", children: "Share report" }), _jsx(Typography, { className: "semrush-share-subtitle", children: "Select users to email this report to." })] }), _jsx(Box, { className: "semrush-share-search", children: _jsx(TextInput, { placeholder: "Search users", value: userSearch, onChange: (e) => setUserSearch(e.target.value), fullWidth: true, leftIcon: _jsx(SearchIcon, { size: 15 }) }) }), _jsx(Box, { className: "semrush-share-list", children: usersLoading ? (_jsx(Box, { className: "semrush-share-loading", children: _jsx(CircularProgress, { size: 22, className: "semrush-share-spinner" }) })) : filteredUsers.length === 0 ? (_jsx(Typography, { className: "semrush-share-empty", children: "No users found" })) : (filteredUsers.map((u) => {
                            const checked = selectedUserIds.includes(u.id);
                            return (_jsxs(Box, { onClick: () => toggleUser(u.id), className: `semrush-share-row${checked ? " selected" : ""}`, children: [_jsx(Checkbox, { checked: checked, size: "small", disableRipple: true, className: "semrush-share-checkbox" }), _jsxs(Box, { className: "semrush-share-user", children: [_jsx(Typography, { className: "semrush-share-name", children: u.name }), _jsx(Typography, { className: "semrush-share-email", children: u.email })] })] }, u.id));
                        })) }), _jsxs(Box, { className: "semrush-share-footer", children: [_jsxs(Typography, { className: "semrush-share-count", children: [selectedUserIds.length, " selected"] }), _jsxs(Box, { className: "semrush-share-actions", children: [_jsx(Button, { variant: "text", color: "inherit", onClick: closeShare, className: "semrush-share-cancel", children: "Cancel" }), _jsx(AppButton, { variant: "contained", color: "primary", onClick: handleShare, loading: sharing, disabled: selectedUserIds.length === 0, startIcon: _jsx(SendIcon, { size: 15 }), children: "Share" })] })] })] }), _jsx(Box, { className: "semrush-tool-results-header", children: _jsxs(Typography, { className: "semrush-tool-results-title", children: ["Search results:", " ", searchedTerm && (_jsx("span", { className: "semrush-tool-results-term", children: searchedTerm }))] }) }), _jsxs(Box, { className: "semrush-tool-card", children: [_jsx(Box, { sx: {
                            display: "flex",
                            backgroundColor: "#FAFAFA",
                            border: "1px solid #e5e7eb",
                            borderRadius: "8px",
                            padding: "4px",
                            width: "fit-content",
                        }, children: SCOPE_TABS.map((tab) => {
                            const isActive = tab.key === scope;
                            return (_jsxs("button", { type: "button", className: "all-Font-Sizes", onClick: () => setScope(tab.key), style: {
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    padding: "8px 16px",
                                    outline: "none",
                                    background: isActive ? "#FFFAFD" : "transparent",
                                    border: isActive ? "1px solid #FFC8E9" : "1px solid transparent",
                                    color: isActive ? "#FF008E" : "#6b7280",
                                    fontWeight: 500,
                                    cursor: "pointer",
                                    borderRadius: "8px",
                                    boxShadow: isActive ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
                                }, children: [_jsx("img", { src: tab.icon, alt: tab.label, style: { width: "18px", height: "13px", objectFit: "cover", borderRadius: "2px", display: "block", flexShrink: 0 } }), _jsx("span", { style: { lineHeight: 1 }, children: tab.label })] }, tab.key));
                        }) }), _jsxs(Box, { className: "semrush-tool-metrics-strip", children: [_jsxs(Box, { className: "semrush-tool-metric-cell semrush-tool-metric-with-icon", children: [_jsx("img", { src: "/assets/wallet.svg", alt: "", style: { width: "20px", height: "20px", filter: "brightness(0) saturate(100%) invert(17%) sepia(96%) saturate(7439%) hue-rotate(311deg) brightness(103%) contrast(101%)" } }), _jsxs(Box, { className: "semrush-tool-metric-text", children: [_jsx(Typography, { className: "semrush-tool-metric-label", sx: { fontSize: '13px !important', fontWeight: '500 !important' }, children: "Authority Score" }), _jsx(Typography, { className: "semrush-tool-metric-value", children: data?.ascore ?? 0 })] })] }), _jsxs(Box, { className: "semrush-tool-metric-cell semrush-tool-metric-cell-right", children: [_jsx(Typography, { className: "semrush-tool-metric-label", sx: { fontSize: '13px !important', fontWeight: '500 !important' }, children: "Organic Search Traffic" }), _jsxs(Box, { className: "semrush-tool-metric-value-row", children: [_jsx(Typography, { className: "semrush-tool-metric-value", children: compactNumber(activeTile.current.organic_traffic) }), _jsx(DeltaChip, { value: activeTile.change.organic_traffic_pct })] })] }), _jsx(Box, { className: "semrush-tool-metric-divider" }), _jsxs(Box, { className: "semrush-tool-metric-cell semrush-tool-metric-with-icon", children: [_jsx("img", { src: "/assets/stopwatch.svg", alt: "", style: { width: "20px", height: "20px", filter: "brightness(0) saturate(100%) invert(17%) sepia(96%) saturate(7439%) hue-rotate(311deg) brightness(103%) contrast(101%)" } }), _jsxs(Box, { className: "semrush-tool-metric-text", children: [_jsx(Typography, { className: "semrush-tool-metric-label", sx: { fontSize: '13px !important', fontWeight: '500 !important' }, children: "SemRush Domain Rank" }), _jsx(Box, { className: "semrush-tool-metric-value-row", children: _jsx(RankDeltaChip, { rank: activeTile.current.rank, delta: activeTile.change.rank_delta }) })] })] }), _jsxs(Box, { className: "semrush-tool-metric-cell semrush-tool-metric-cell-right", children: [_jsx(Typography, { className: "semrush-tool-metric-label", sx: { fontSize: '13px !important', fontWeight: '500 !important' }, children: "Keywords" }), _jsxs(Box, { className: "semrush-tool-metric-value-row", children: [_jsx(Typography, { className: "semrush-tool-metric-value", children: compactNumber(activeTile.current.organic_keyword) }), _jsx(DeltaChip, { value: activeTile.change.organic_keyword_pct })] })] })] })] }), _jsxs(Box, { className: "semrush-tool-card", children: [_jsxs(Box, { className: "semrush-tool-distribution-bar", children: [_jsx(Typography, { className: "semrush-tool-distribution-title", sx: { fontSize: '13px !important', fontWeight: '700 !important' }, children: "Distribution by Country" }), _jsx(CommonTabs, { selectedValue: distributionMode, onChange: setDistributionMode, tabs: [
                                    { label: "Organic", value: "organic" },
                                    { label: "Paid", value: "paid" },
                                ] })] }), _jsx(Box, { sx: {
                            bgcolor: "#fff",
                            overflow: "hidden",
                            border: "1px solid #e5e7eb",
                            borderRadius: "8px",
                        }, children: _jsx(CommonTable, { columns: columns, rows: distributionRows, renderCell: renderCell, isLoading: loading, expandTrigger: "none" }) })] })] }));
};
export default SemrushToolMain;
